import React, { createContext, useContext, useState, useEffect } from 'react';
import TokenExpiryModal from '../user_panel/components/TokenExpiryModal';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [user, setUser] = useState(null);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState(false);

  // Функция для проверки истечения токена
  const checkTokenExpiry = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      // Декодируем payload токена
      const payload = token.split('.')[1];
      if (!payload) return null;

      const decodedPayload = JSON.parse(atob(payload));
      const expirationTime = decodedPayload.exp * 1000; // Конвертируем в миллисекунды
      const currentTime = Date.now();
      const timeRemaining = expirationTime - currentTime;

      return {
        isExpired: timeRemaining <= 0,
        timeRemaining: Math.max(0, timeRemaining),
        expirationTime
      };
    } catch (error) {
      console.error('Ошибка при проверке токена:', error);
      return null;
    }
  };

  // Функция для загрузки данных пользователя из localStorage
  const loadUserFromStorage = () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const libraryCard = localStorage.getItem('libraryCard');

    if (!token || !role || !libraryCard) {
      return;
    }

    // Проверяем токен
    const tokenInfo = checkTokenExpiry();
    if (!tokenInfo || tokenInfo.isExpired) {
      // Токен истек, очищаем данные
      clearAuthData();
      return;
    }

    if (role === 'admin') {
      const adminId = localStorage.getItem('adminId');
      const name = localStorage.getItem('name');
      const email = localStorage.getItem('email');

      if (adminId && name) {
        setAdmin({
          token,
          role,
          adminId,
          name,
          email,
          libraryCard
        });
      }
    } else if (role === 'user') {
      const userId = localStorage.getItem('userId');
      const firstName = localStorage.getItem('firstName');
      const lastName = localStorage.getItem('lastName');
      const email = localStorage.getItem('email');
      const avatar_url = localStorage.getItem('avatar_url');
      
      if (userId && firstName && lastName) {
        setUser({
          token,
          role,
          userId,
          firstName,
          lastName,
          middleName: localStorage.getItem('middleName'),
          email,
          libraryCard,
          avatar_url
        });
      }
    }
  };

  // Функция для очистки данных авторизации
  const clearAuthData = () => {
    setAdmin(null);
    setUser(null);
    setShouldRedirectToLogin(true);
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('adminId');
    localStorage.removeItem('userId');
    localStorage.removeItem('name');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('middleName');
    localStorage.removeItem('email');
    localStorage.removeItem('libraryCard');
    localStorage.removeItem('avatar_url');
  };

  // Загружаем пользователя при инициализации
  useEffect(() => {
    loadUserFromStorage();
  }, []);

  // Проверяем истечение токена каждую минуту
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token) return;

      const tokenInfo = checkTokenExpiry();
      if (!tokenInfo) return;

      if (tokenInfo.isExpired) {
        // Токен истек
        setShowExpiryModal(false);
        clearAuthData();
      } else if (tokenInfo.timeRemaining <= 5 * 60 * 1000) { // 5 минут до истечения
        // Показываем предупреждение за 5 минут до истечения
        if (!showExpiryModal) {
          setTimeLeft(tokenInfo.timeRemaining);
          setShowExpiryModal(true);
        } else {
          setTimeLeft(tokenInfo.timeRemaining);
        }
      }
    }, 30000); // Проверяем каждые 30 секунд

    return () => clearInterval(checkInterval);
  }, [showExpiryModal]);

  // Слушаем изменения localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'role') {
        loadUserFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = (token, userData) => {
    // Проверяем валидность токена перед сохранением
    localStorage.setItem('token', token); // Временно сохраняем для проверки
    const tokenInfo = checkTokenExpiry();
    if (tokenInfo && tokenInfo.isExpired) {
      console.error('Попытка войти с истекшим токеном');
      localStorage.removeItem('token');
      return;
    }

    setShouldRedirectToLogin(false); // Сбрасываем флаг редиректа

    if (userData.role === 'admin') {
      setAdmin({
        token,
        role: userData.role,
        adminId: userData.id,
        name: userData.name,
        email: userData.email,
        libraryCard: userData.libraryCard
      });
      setUser(null);

      localStorage.setItem('role', userData.role);
      localStorage.setItem('adminId', userData.id);
      localStorage.setItem('name', userData.name);
      localStorage.setItem('email', userData.email);
      localStorage.setItem('libraryCard', userData.libraryCard);
    } else if (userData.role === 'user') {
      setUser({
        token,
        role: userData.role,
        userId: userData.id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        middleName: userData.middleName,
        email: userData.email,
        libraryCard: userData.libraryCard,
        avatar_url: userData.avatar_url
      });
      setAdmin(null);

      localStorage.setItem('role', userData.role);
      localStorage.setItem('userId', userData.id);
      localStorage.setItem('firstName', userData.firstName);
      localStorage.setItem('lastName', userData.lastName);
      localStorage.setItem('email', userData.email);
      localStorage.setItem('libraryCard', userData.libraryCard);
      localStorage.setItem('avatar_url', userData.avatar_url);
      if (userData.middleName) {
        localStorage.setItem('middleName', userData.middleName);
      }
    }

    // Закрываем модальное окно если оно было открыто
    setShowExpiryModal(false);
  };

  const logout = () => {
    setShowExpiryModal(false);
    clearAuthData();
  };

  const handleRelogin = () => {
    setShowExpiryModal(false);
    clearAuthData();
  };

  // Функция для проверки авторизации (для использования в компонентах)
  const checkAuth = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    const tokenInfo = checkTokenExpiry();
    if (!tokenInfo || tokenInfo.isExpired) {
      clearAuthData();
      return false;
    }

    return true;
  };

  // Функция для сброса флага редиректа (вызывается из компонентов после навигации)
  const resetRedirectFlag = () => {
    setShouldRedirectToLogin(false);
  };

  const value = {
    admin,
    user,
    login,
    logout,
    checkAuth,
    resetRedirectFlag,
    isAuthenticated: !!(admin || user),
    isAdmin: !!admin,
    isUser: !!user,
    shouldRedirectToLogin,
    tokenExpiry: checkTokenExpiry()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <TokenExpiryModal
        show={showExpiryModal}
        onRelogin={handleRelogin}
        onLogout={logout}
        timeLeft={timeLeft}
      />
    </AuthContext.Provider>
  );
};